import { registerLocaleData } from '@angular/common';
import { ApplicationConfig, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, importProvidersFrom, inject } from '@angular/core';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DreambooksApiModule, Frontend } from '@dreambooks/api';
import { DreambooksRendererModule, PROJECT_PHOTOS_PROVIDER, RendererService } from '@dreambooks/renderer';

// Register locale data for Angular i18n pipes and @dreambooks/renderer MonthElementComponent
import localePtPt from '@angular/common/locales/pt-PT';
import localePt from '@angular/common/locales/pt';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
registerLocaleData(localePtPt, 'pt-PT');
registerLocaleData(localePtPt, 'pt_PT');
registerLocaleData(localePt, 'pt-BR');
registerLocaleData(localePt, 'pt_BR');
registerLocaleData(localeEnGb, 'en-GB');
registerLocaleData(localeEnGb, 'en_US');
registerLocaleData(localeEs, 'es-ES');
registerLocaleData(localeFr, 'fr-FR');
registerLocaleData(localeDe, 'de-DE');
registerLocaleData(localeDe, 'de_DE');
registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeIt, 'it_IT');
registerLocaleData(localeNl, 'nl-NL'); // BCP 47 alias for Angular/web formatting
registerLocaleData(localeNl, 'nl_NL'); // Canonical Express locale

import { routes } from './app.routes';
import { CachedRendererService } from './features/editor/services/cached-renderer.service';
import { CustomRouteReuseStrategy } from './core/services/custom-route-reuse-strategy';
import { ClientDiagnosticsService } from './core/services/client-diagnostics.service';
import { ReactivePhotosDebugInterceptor } from './core/interceptors/reactivephotos-debug.interceptor';
import { ErrorRecordingInterceptor } from './core/interceptors/error-recording.interceptor';
import { PermissionErrorInterceptor } from './core/interceptors/permission-error.interceptor';

// Factory for ngx-translate loader
// Explicitly set the path and file extension for translation files
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    importProvidersFrom(
      TranslateModule.forRoot({
        fallbackLang: 'pt_PT',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }),
      DreambooksApiModule.forRoot({
        apiUrl: '/api',
        frontend: 'web' as Frontend,
        debug: false,
        billingCountryCode: 'PT',
        locale: 'pt_PT'
      }),
      DreambooksRendererModule.forRoot({
        assetsUrl: './assets/renderer',
        exportDpi: 254,
        placeholderImage: 'img_placeholder_unified.svg',
        placeholderColor: '#e1e1e1',
        pageThumbnailHeight: 80,
        // Hard rule: never allow renderer to pick original photo URL.
        allowOriginalPhotoUrl: false
      })
    ),
    PROJECT_PHOTOS_PROVIDER,
    // Replace RendererService with CachedRendererService for image caching
    { provide: RendererService, useClass: CachedRendererService },
    // Custom RouteReuseStrategy to preserve album form state when navigating to/from editor
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: ReactivePhotosDebugInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorRecordingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: PermissionErrorInterceptor, multi: true },
    // Force ClientDiagnosticsService to instantiate at app startup so its
    // window.error / unhandledrejection listeners attach before any code
    // has a chance to throw. Without this, the service would only be
    // built lazily when the first HTTP error fires.
    provideAppInitializer(() => { inject(ClientDiagnosticsService); }),
  ]
};
